import { Check, Eye, Lock, LockOpen, Sparkles, Trash2, X } from "lucide-react";
import { useCallback, useState } from "react";
import { fetchJson, sendJson } from "../../api/rest";
import type {
	CategoryRef,
	EditorState,
	ProductRef,
	RecommendationStatus,
	Suggestion,
} from "../../types/recommendations";
import { OrderedPicker, type PickerItem } from "./OrderedPicker";

const STATUSES: RecommendationStatus[] = ["draft", "approved", "published"];

const searchProducts = async (q: string): Promise<PickerItem[]> => {
	const rows = await fetchJson<ProductRef[]>(
		`/recommendations/product-search?q=${encodeURIComponent(q)}`,
	);
	return rows.map((r) => ({ id: r.productId, label: r.name, sub: r.gtin }));
};

const searchCategories = async (q: string): Promise<PickerItem[]> => {
	const rows = await fetchJson<CategoryRef[]>(
		`/recommendations/category-search?q=${encodeURIComponent(q)}`,
	);
	return rows.map((r) => ({ id: r.categoryId, label: r.name }));
};

interface Props {
	state: EditorState;
	onChange: (next: EditorState) => void;
	onSave: () => Promise<void>;
	onDelete: () => Promise<void>;
	onClose: () => void;
	saving: boolean;
}

export const RecommendationEditor = ({
	state,
	onChange,
	onSave,
	onDelete,
	onClose,
	saving,
}: Props) => {
	const [suggesting, setSuggesting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showSnapshot, setShowSnapshot] = useState(false);
	const [synonymDraft, setSynonymDraft] = useState("");

	const patch = useCallback(
		(fields: Partial<EditorState>) => onChange({ ...state, ...fields }),
		[state, onChange],
	);

	// Ask backend-rust for a proposal and load it into the editor. It replaces the
	// current selection, so the operator can always re-edit before saving.
	const suggest = useCallback(async () => {
		const query = state.query.trim();
		if (!query) return;
		setSuggesting(true);
		setError(null);
		try {
			const s = await sendJson<Suggestion>("/recommendations/suggest", "POST", {
				query,
			});

			const nameFor = (id: number) =>
				s.candidateProducts.find((c) => c.productId === id)?.productName ??
				`#${id}`;

			patch({
				products: s.orderedProductIds.map((id) => ({
					productId: id,
					name: nameFor(id),
				})),
				categories: s.categoryIds.map((id) => ({
					categoryId: id,
					name: s.categories.find((c) => c.categoryId === id)?.name ?? `#${id}`,
				})),
				synonyms: s.synonyms,
				intent: s.intent,
				candidateSnapshot: s.candidateProducts,
				model: s.model ?? null,
			});

			if (s.orderedProductIds.length === 0) {
				setError("The model returned no products for this query.");
			}
		} catch (e) {
			setError(
				e instanceof Error ? e.message.slice(0, 300) : "Suggestion failed",
			);
		} finally {
			setSuggesting(false);
		}
	}, [state.query, patch]);

	const addSynonym = useCallback(() => {
		const value = synonymDraft.trim().toLowerCase();
		if (!value || state.synonyms.includes(value)) return;
		patch({ synonyms: [...state.synonyms, value] });
		setSynonymDraft("");
	}, [synonymDraft, state.synonyms, patch]);

	return (
		<div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
			<div className="flex items-start justify-between gap-3">
				<div className="flex-1">
					<label
						htmlFor="rec-query"
						className="mb-1 block text-xs font-medium text-slate-500"
					>
						Search query
					</label>
					<input
						id="rec-query"
						value={state.query}
						onChange={(e) => patch({ query: e.target.value })}
						disabled={state.id !== null}
						placeholder="e.g. trådløse høretelefoner"
						className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary disabled:bg-slate-50 disabled:text-slate-500"
					/>
					{state.id !== null && (
						<p className="mt-1 text-xs text-slate-400">
							The query is the row's key and cannot be changed. Delete and
							recreate to rename.
						</p>
					)}
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close editor"
					className="mt-5 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"
				>
					<X className="h-4 w-4" />
				</button>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<button
					type="button"
					onClick={suggest}
					disabled={suggesting || !state.query.trim()}
					className="flex items-center gap-1.5 rounded-full bg-brand hover:bg-brand-hover px-3 py-1.5 text-[13px] font-semibold text-black disabled:opacity-50"
				>
					<Sparkles className="h-4 w-4" />
					{suggesting ? "Thinking…" : "Suggest with AI"}
				</button>
				<button
					type="button"
					onClick={() => patch({ locked: !state.locked })}
					className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-medium ${
						state.locked
							? "border-amber-200 bg-amber-50 text-amber-700"
							: "border-slate-200 text-slate-500"
					}`}
					title={
						state.locked
							? "Locked — the scheduled job will not modify this entry"
							: "Unlocked — the scheduled job may overwrite this draft"
					}
				>
					{state.locked ? (
						<Lock className="h-4 w-4" />
					) : (
						<LockOpen className="h-4 w-4" />
					)}
					{state.locked ? "Locked" : "Unlocked"}
				</button>
				<select
					value={state.status}
					onChange={(e) =>
						patch({ status: e.target.value as RecommendationStatus })
					}
					aria-label="Status"
					className="rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] text-slate-700 outline-none focus:border-primary"
				>
					{STATUSES.map((s) => (
						<option key={s} value={s}>
							{s}
						</option>
					))}
				</select>
				{state.model && (
					<span className="text-xs text-slate-400">
						proposed by {state.model}
					</span>
				)}
			</div>

			{state.status === "draft" && (
				<p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
					Drafts are not served. Set the status to <strong>approved</strong> to
					make this live on the storefront.
				</p>
			)}

			{error && (
				<p className="rounded-lg bg-rose-50 px-3 py-2 text-[13px] text-rose-600">
					{error}
				</p>
			)}

			<OrderedPicker
				title="Products"
				hint="Search products by name or GTIN…"
				items={state.products.map((p) => ({ id: p.productId, label: p.name }))}
				onChange={(items) =>
					patch({
						products: items.map((i) => ({ productId: i.id, name: i.label })),
					})
				}
				search={searchProducts}
			/>

			<OrderedPicker
				title="Suggested categories"
				hint="Search categories…"
				items={state.categories.map((c) => ({
					id: c.categoryId,
					label: c.name,
				}))}
				onChange={(items) =>
					patch({
						categories: items.map((i) => ({ categoryId: i.id, name: i.label })),
					})
				}
				search={searchCategories}
			/>

			<div className="space-y-2">
				<h4 className="text-sm font-semibold text-slate-700">Synonyms</h4>
				<div className="flex flex-wrap gap-1.5">
					{state.synonyms.map((s) => (
						<span
							key={s}
							className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
						>
							{s}
							<button
								type="button"
								aria-label={`Remove ${s}`}
								onClick={() =>
									patch({ synonyms: state.synonyms.filter((x) => x !== s) })
								}
								className="text-slate-400 hover:text-rose-500"
							>
								<X className="h-3 w-3" />
							</button>
						</span>
					))}
					{state.synonyms.length === 0 && (
						<span className="text-[13px] text-slate-400">None</span>
					)}
				</div>
				<input
					value={synonymDraft}
					onChange={(e) => setSynonymDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							addSynonym();
						}
					}}
					placeholder="Add a synonym and press Enter…"
					className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-primary"
				/>
			</div>

			<div className="grid gap-3 sm:grid-cols-2">
				<div>
					<label
						htmlFor="rec-intent"
						className="mb-1 block text-xs font-medium text-slate-500"
					>
						Intent
					</label>
					<input
						id="rec-intent"
						value={state.intent}
						onChange={(e) => patch({ intent: e.target.value })}
						placeholder="What is the shopper after?"
						className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-primary"
					/>
				</div>
				<div>
					<label
						htmlFor="rec-notes"
						className="mb-1 block text-xs font-medium text-slate-500"
					>
						Notes
					</label>
					<input
						id="rec-notes"
						value={state.notes}
						onChange={(e) => patch({ notes: e.target.value })}
						placeholder="Why this ordering?"
						className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-primary"
					/>
				</div>
			</div>

			{state.candidateSnapshot.length > 0 && (
				<div>
					<button
						type="button"
						onClick={() => setShowSnapshot((v) => !v)}
						className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700"
					>
						<Eye className="h-3.5 w-3.5" />
						{showSnapshot ? "Hide" : "Show"} what the model saw (
						{state.candidateSnapshot.length} candidates)
					</button>
					{showSnapshot && (
						<table className="mt-2 w-full text-left text-xs">
							<thead>
								<tr className="border-b border-slate-200 text-slate-500">
									<th className="pb-1 font-medium">Product</th>
									<th className="pb-1 font-medium">Clicks</th>
									<th className="pb-1 font-medium">Avg. position</th>
								</tr>
							</thead>
							<tbody>
								{state.candidateSnapshot.map((c) => (
									<tr
										key={c.productId}
										className="border-b border-slate-100 last:border-0"
									>
										<td className="py-1 pr-2 text-slate-600">
											{c.productName ?? `#${c.productId}`}
										</td>
										<td className="py-1 pr-2 text-slate-500">{c.clicks}</td>
										<td className="py-1 text-slate-500">
											{c.avgPosition || "—"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			)}

			<div className="flex items-center gap-2 border-t border-slate-100 pt-3">
				<button
					type="button"
					onClick={onSave}
					disabled={
						saving || !state.query.trim() || state.products.length === 0
					}
					className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
				>
					<Check className="h-4 w-4" />
					{saving ? "Saving…" : "Save"}
				</button>
				{state.id !== null && (
					<button
						type="button"
						onClick={onDelete}
						className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] font-medium text-slate-500 hover:border-rose-200 hover:text-rose-500"
					>
						<Trash2 className="h-4 w-4" />
						Delete
					</button>
				)}
				{state.products.length === 0 && (
					<span className="text-xs text-slate-400">
						Add at least one product to save.
					</span>
				)}
			</div>
		</div>
	);
};
